import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';

// React Toastify Imports
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

// MUI Joy Components
import {
    Breadcrumbs, Link, Select, Option, Card, Grid, CircularProgress,
    Alert, Stack, Divider, Box, Typography, Tabs, TabList, Tab, TabPanel,
    Button, Sheet, Table, tabClasses, Chip, Tooltip, Modal, ModalDialog,
    ModalClose, IconButton, List, ListItem, ListItemContent
} from "@mui/joy";

// Icons
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import MonetizationOnRoundedIcon from '@mui/icons-material/MonetizationOnRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import AccessTimeFilledRoundedIcon from '@mui/icons-material/AccessTimeFilledRounded';
import ReportIcon from '@mui/icons-material/Report';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import TextsmsIcon from '@mui/icons-material/Textsms';
import ShutterSpeedIcon from '@mui/icons-material/ShutterSpeed';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import SaveIcon from '@mui/icons-material/Save';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Local Imports
import BookingApiService from "../../axios/Bookings-Axios";
import AttemptedLeadsTable from "../tables/AttemptedLeadsTable";
import GeneralApiService from "../../axios/Axios";

// --- Helper Functions ---

function formatSecondsToHMS(seconds) {
    if (seconds === null || seconds < 0) return 'N/A';
    if (seconds === 0) return 'Instant';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return [h > 0 ? `${h}h` : '', m > 0 ? `${m}m` : '', s > 0 ? `${s}s` : ''].filter(Boolean).join(' ');
}

function formatCurrency(amount) {
    return (amount || 0).toLocaleString("en-GB", { style: "currency", currency: "GBP" });
}

function StatCard({ icon, title, value, color = 'primary' }) {
    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{
                width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0, bgcolor: `${color}.softBg`, color: `${color}.solidColor`
            }}>
                {icon}
            </Box>
            <Box>
                <Typography level="body-sm">{title}</Typography>
                <Typography level="h3">{value}</Typography>
            </Box>
        </Box>
    );
}

function PayoutTimer() {
    const [timeLeft, setTimeLeft] = useState('Loading...');
    useEffect(() => {
        const calculateTimeLeft = () => {
            const now = new Date();
            const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
            const difference = nextMonth.getTime() - now.getTime();
            let timeLeftString = "Payout processing!";
            if (difference > 0) {
                const days = Math.floor(difference / (1000 * 60 * 60 * 24));
                const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
                const minutes = Math.floor((difference / 1000 / 60) % 60);
                timeLeftString = `${days}d ${hours}h ${minutes}m`;
            }
            setTimeLeft(timeLeftString);
        };
        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <Typography level="title-sm" startDecorator={<AccessTimeFilledRoundedIcon />}>
            Next Payout: {timeLeft}
        </Typography>
    );
}

function WeeklyPayrollTable({ weeklyData }) {
    const [selectedWeek, setSelectedWeek] = useState(null);

    if (!weeklyData || weeklyData.length === 0) return null;

    return (
        <>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden', mt: 3 }}>
                <Table size="sm" hoverRow>
                    <thead>
                        <tr>
                            <th style={{ width: '35%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                    Week (Sat - Fri)
                                </Box>
                            </th>
                            <th style={{ textAlign: 'center' }}>Bookings</th>
                            <th style={{ textAlign: 'center' }}>Split</th>
                            <th style={{ textAlign: 'right' }}>Commission</th>
                            <th style={{ textAlign: 'right' }}>Override</th>
                            <th style={{ textAlign: 'center' }}>Cancellations</th>
                            <th style={{ textAlign: 'right', fontWeight: 700 }}>Total to Pay</th>
                            <th style={{ textAlign: 'center', width: 50 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {weeklyData.map((week, idx) => (
                            <tr key={idx} style={{ backgroundColor: week.isCurrent ? 'var(--joy-palette-warning-softBg)' : undefined }}>
                                <td>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography level="body-sm" fontWeight={week.isCurrent ? 600 : 400}>
                                            {week.label}
                                        </Typography>
                                        {week.isCurrent && (
                                            <Chip size="sm" color="warning" variant="soft">Current</Chip>
                                        )}
                                    </Box>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <Typography level="body-sm">{week.bookingsCompleted}</Typography>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    <Typography level="body-sm" color={week.splitBookings > 0 ? 'warning' : 'neutral'}>
                                        {week.splitBookings || 0}
                                    </Typography>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    <Typography level="body-sm" color="success">
                                        {formatCurrency(week.commissionEarned)}
                                    </Typography>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    {week.overrideEarned > 0 ? (
                                        <Tooltip
                                            title={
                                                <Box sx={{ p: 0.5 }}>
                                                    <Typography level="body-xs" fontWeight={600} sx={{ mb: 0.5 }}>Override Breakdown:</Typography>
                                                    {week.overrideDetails.map((d, i) => (
                                                        <Typography key={i} level="body-xs">
                                                            {d.username}: {d.commissions} booking{d.commissions !== 1 ? 's' : ''} = {formatCurrency(d.overrideAmount)}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            }
                                            placement="left"
                                            arrow
                                            variant="outlined"
                                            sx={{
                                                bgcolor: 'background.surface',
                                                color: 'text.primary',
                                                boxShadow: 'md',
                                                borderColor: 'neutral.outlinedBorder',
                                            }}
                                        >
                                            <Box component="span" sx={{ display: 'inline-block' }}>
                                                <Typography level="body-sm" color="primary" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                                                    {formatCurrency(week.overrideEarned)}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    ) : (
                                        <Typography level="body-sm" color="neutral">—</Typography>
                                    )}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    {(() => {
                                        const total = (week.cancelledBookings?.length || 0) + (week.deletedBookings?.length || 0);
                                        const hasDeductions = week.deductionDetails?.length > 0;
                                        if (total > 0 || hasDeductions) {
                                            return (
                                                <Tooltip
                                                    title={
                                                        <Box sx={{ p: 0.5 }}>
                                                            {(week.cancelledBookings?.length || 0) > 0 && (
                                                                <Typography level="body-xs">{week.cancelledBookings.length} cancelled</Typography>
                                                            )}
                                                            {(week.deletedBookings?.length || 0) > 0 && (
                                                                <Typography level="body-xs">{week.deletedBookings.length} deleted</Typography>
                                                            )}
                                                            {hasDeductions && (
                                                                <Typography level="body-xs" color="danger">{week.deductionDetails.length} deduction{week.deductionDetails.length !== 1 ? 's' : ''}</Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    placement="left"
                                                    arrow
                                                    variant="outlined"
                                                >
                                                    <Typography level="body-sm" color={hasDeductions ? 'danger' : 'warning'} sx={{ cursor: 'help' }}>
                                                        {total + (week.deductionDetails?.length || 0)}
                                                    </Typography>
                                                </Tooltip>
                                            );
                                        }
                                        return <Typography level="body-sm" color="neutral">—</Typography>;
                                    })()}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    <Typography level="body-sm" fontWeight={700} color={week.totalEarned > 0 ? 'success' : 'neutral'}>
                                        {formatCurrency(week.totalEarned)}
                                    </Typography>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                    {(week.bookingDetails?.length > 0 || week.splitBookingDetails?.length > 0 || week.cancelledBookings?.length > 0 || week.deletedBookings?.length > 0 || week.deductionDetails?.length > 0) && (
                                        <IconButton size="sm" variant="plain" color="primary" onClick={() => setSelectedWeek(week)}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>

            {/* Booking Details Modal */}
            <Modal open={!!selectedWeek} onClose={() => setSelectedWeek(null)}>
                <ModalDialog sx={{ 
                    maxWidth: { xs: '95vw', sm: 500 }, 
                    width: { xs: '95vw', sm: 'auto' },
                    maxHeight: '80vh', 
                    overflow: 'auto' 
                }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>
                        Bookings for {selectedWeek?.label}
                    </Typography>
                    
                    {selectedWeek?.bookingDetails?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>Bookings ({selectedWeek.bookingDetails.length})</Typography>
                            <List size="sm">
                                {selectedWeek.bookingDetails.map((booking, i) => (
                                    <ListItem key={i}>
                                        <ListItemContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography level="body-sm" fontWeight={500}>
                                                        {booking.customerName}
                                                        {booking.isFranchiseOwner && (
                                                            <Typography component="span" level="body-xs" sx={{ color: 'neutral.400', ml: 1, fontWeight: 400 }}>
                                                                (franchise owner)
                                                            </Typography>
                                                        )}
                                                        {booking.isSplit && <Chip size="sm" color="warning" variant="soft" sx={{ ml: 1 }}>Split</Chip>}
                                                        {booking.isOverrideSplit && <Chip size="sm" color="primary" variant="soft" sx={{ ml: 0.5 }}>Override</Chip>}
                                                    </Typography>
                                                    <Typography level="body-xs" color="neutral">
                                                        Created on {new Date(booking.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                    </Typography>
                                                </Box>
                                                <Typography level="body-sm" color="success">
                                                    {formatCurrency(booking.commission)}
                                                </Typography>
                                            </Box>
                                        </ListItemContent>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {selectedWeek?.splitBookingDetails?.length > 0 && (
                        <Box>
                            <Typography level="title-md" sx={{ mb: 1 }}>Split Bookings (Assist) ({selectedWeek.splitBookingDetails.length})</Typography>
                            <List size="sm">
                                {selectedWeek.splitBookingDetails.map((booking, i) => (
                                    <ListItem key={i}>
                                        <ListItemContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography level="body-sm" fontWeight={500}>
                                                        {booking.customerName}
                                                        {booking.isFranchiseOwner && (
                                                            <Typography component="span" level="body-xs" sx={{ color: 'neutral.400', ml: 1, fontWeight: 400 }}>
                                                                (franchise owner)
                                                            </Typography>
                                                        )}
                                                        {booking.isOverrideSplit && <Chip size="sm" color="primary" variant="soft" sx={{ ml: 0.5 }}>Override</Chip>}
                                                    </Typography>
                                                    <Typography level="body-xs" color="neutral">
                                                        Created on {new Date(booking.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                    </Typography>
                                                    {booking.isOverrideSplit && booking.overrideAmount > 0 && (
                                                        <Typography level="body-xs" color="primary">
                                                            Split: {formatCurrency(booking.commission)} + Override: {formatCurrency(booking.overrideAmount)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    {booking.isOverrideSplit && booking.overrideAmount > 0 ? (
                                                        <Typography level="body-sm" color="success">
                                                            {formatCurrency(booking.commission + booking.overrideAmount)}
                                                        </Typography>
                                                    ) : (
                                                        <Typography level="body-sm" color="warning">
                                                            {formatCurrency(booking.commission)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </ListItemContent>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {selectedWeek?.cancelledBookings?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography level="title-md" sx={{ mb: 1 }} color="warning">Cancelled ({selectedWeek.cancelledBookings.length})</Typography>
                            <List size="sm">
                                {selectedWeek.cancelledBookings.map((booking, i) => (
                                    <ListItem key={i}>
                                        <ListItemContent>
                                            <Typography level="body-sm" fontWeight={500}>
                                                {booking.customerName}
                                                {booking.isFranchiseOwner && (
                                                    <Typography component="span" level="body-xs" sx={{ color: 'neutral.400', ml: 1, fontWeight: 400 }}>
                                                        (franchise owner)
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Typography level="body-xs" color="neutral">
                                                Created on {new Date(booking.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                            </Typography>
                                            {booking.cancelledAt && (
                                                <Typography level="body-xs" color="neutral">
                                                    Cancelled on {new Date(booking.cancelledAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                </Typography>
                                            )}
                                        </ListItemContent>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {selectedWeek?.deletedBookings?.length > 0 && (
                        <Box>
                            <Typography level="title-md" sx={{ mb: 1 }} color="danger">Deleted ({selectedWeek.deletedBookings.length})</Typography>
                            <List size="sm">
                                {selectedWeek.deletedBookings.map((booking, i) => (
                                    <ListItem key={i}>
                                        <ListItemContent>
                                            <Typography level="body-sm" fontWeight={500}>
                                                {booking.customerName}
                                                {booking.isFranchiseOwner && (
                                                    <Typography component="span" level="body-xs" sx={{ color: 'neutral.400', ml: 1, fontWeight: 400 }}>
                                                        (franchise owner)
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Typography level="body-xs" color="neutral">
                                                Created on {new Date(booking.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                            </Typography>
                                            {booking.cancelledAt && (
                                                <Typography level="body-xs" color="neutral">
                                                    Deleted on {new Date(booking.cancelledAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                </Typography>
                                            )}
                                        </ListItemContent>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {selectedWeek?.deductionDetails?.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography level="title-md" sx={{ mb: 1 }} color="danger">Deductions (Paid Previously) ({selectedWeek.deductionDetails.length})</Typography>
                            <List size="sm">
                                {selectedWeek.deductionDetails.map((deduction, i) => (
                                    <ListItem key={i}>
                                        <ListItemContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography level="body-sm" fontWeight={500}>
                                                        {deduction.customerName}
                                                    </Typography>
                                                    <Typography level="body-xs" color="neutral">
                                                        Created on {new Date(deduction.bookingCreatedAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                    </Typography>
                                                    <Typography level="body-xs" color="neutral">
                                                        Cancelled on {new Date(deduction.cancelledAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                    </Typography>
                                                </Box>
                                                <Typography level="body-sm" color="danger">
                                                    -{formatCurrency(deduction.amount)}
                                                </Typography>
                                            </Box>
                                        </ListItemContent>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {(!selectedWeek?.bookingDetails?.length && !selectedWeek?.splitBookingDetails?.length && !selectedWeek?.cancelledBookings?.length && !selectedWeek?.deletedBookings?.length && !selectedWeek?.deductionDetails?.length) && (
                        <Typography level="body-sm" color="neutral">No bookings for this week.</Typography>
                    )}
                </ModalDialog>
            </Modal>
        </>
    );
}

// --- Sub-Components for Tabs ---

const PerformanceTab = ({ loading, error, salesData, selectedMonth, setSelectedMonth, formatMonthForDisplay, weeklyData, summary }) => {
    // monthlyStats is inside salesData object now
    const monthlyStats = salesData?.monthlyStats || {};
    // Convert object to array for dropdown
    const monthlyOptions = Object.keys(monthlyStats).map(key => ({
        value: key,
        label: formatMonthForDisplay(key)
    }));
    
    // Get current month data
    const currentMonthData = monthlyStats[selectedMonth];

    return (
        <Stack spacing={3}>
            {/* Header with Month Selector */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography level="h4">Performance Dashboard</Typography>
                {!loading && monthlyOptions.length > 0 && (
                    <Select value={selectedMonth} onChange={(_, nv) => setSelectedMonth(nv)} size="sm" sx={{ minWidth: 180 }}>
                        {monthlyOptions.map((opt) => (<Option key={opt.value} value={opt.value}>{opt.label}</Option>))}
                    </Select>
                )}
            </Box>

            {loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>)
                : error ? (<Alert color="danger" variant="soft" startDecorator={<ReportIcon />}>{error}</Alert>)
                    : (
                        <>
                            {/* Summary Cards */}
                            <Grid container spacing={2}>
                                {/* KPI Group 1: Core Financials */}
                                <Grid xs={12} md={4}>
                                    <Card variant="outlined">
                                        <Typography level="title-md">Financials</Typography>
                                        <Stack spacing={2} mt={2}>
                                            <StatCard icon={<MonetizationOnRoundedIcon />} title="Commission Earned" value={formatCurrency(currentMonthData?.totalEarned)} color="success" />
                                            <StatCard icon={<BookOnlineIcon />} title="Total Booked In" value={currentMonthData?.totalBookingsBooked || 0} color="neutral" />
                                            <StatCard icon={<TaskAltRoundedIcon />} title="Bookings Not Cancelled" value={currentMonthData?.totalBookingsCompleted || 0} color="primary" />
                                        </Stack>
                                    </Card>
                                </Grid>
                                {/* KPI Group 2: Sales Activity */}
                                <Grid xs={12} md={4}>
                                    <Card variant="outlined">
                                        <Typography level="title-md">Sales Activity</Typography>
                                        <Stack spacing={2} mt={2}>
                                            <StatCard icon={<PhoneForwardedIcon />} title="Outbound Calls Made" value={currentMonthData?.callsMade || 0} color="info" />
                                            <StatCard icon={<PhoneCallbackIcon />} title="Calls Answered" value={currentMonthData?.callsAnswered || 0} color="success" />
                                            <StatCard icon={<TextsmsIcon />} title="Texts Sent" value={currentMonthData?.textsMade || 0} color="warning" />
                                        </Stack>
                                    </Card>
                                </Grid>
                                {/* KPI Group 3: Performance Metrics */}
                                <Grid xs={12} md={4}>
                                    <Card variant="outlined">
                                        <Typography level="title-md">Performance</Typography>
                                        <Stack spacing={2} mt={2}>
                                            <StatCard icon={<TrackChangesIcon />} title="Attempted Conversions" value={currentMonthData?.totalAttemptedConversions || 0} color="primary" />
                                            <StatCard icon={<QueryStatsRoundedIcon />} title="Conversion Rate" value={`${(currentMonthData?.conversionRateTotal || 0).toFixed(1)}%`} color="warning" />
                                            <StatCard icon={<ShutterSpeedIcon />} title="Avg. Speed to Lead" value={formatSecondsToHMS(currentMonthData?.averageSpeedToLead)} color="danger" />
                                        </Stack>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Weekly Payroll Table */}
                            {weeklyData && (
                                <Box>
                                    <Typography level="h4" startDecorator={<CalendarTodayIcon />}>Weekly Payroll</Typography>
                                    <WeeklyPayrollTable weeklyData={weeklyData} />
                                </Box>
                            )}

                            {/* Attempted Leads Table */}
                            {currentMonthData && (
                                <Box>
                                    <Typography level="h4" component="h2">Attempted Leads for {formatMonthForDisplay(selectedMonth)}</Typography>
                                    <Typography level={"body-xs"} sx={{ color: "red" }}>* Commission is only given for bookings YOU created</Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <AttemptedLeadsTable isLoading={loading} leads={currentMonthData.attemptedLeadsDetails} />
                                </Box>
                            )}
                        </>
                    )
            }
        </Stack>
    );
};

const ScheduleTab = () => {
    const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const HOURS = Array.from({ length: 24 }, (_, i) => i);

    const [scheduleMatrix, setScheduleMatrix] = useState({}); // { dayIndex: Set(hours) }
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSchedule();
    }, []);

    const fetchSchedule = async () => {
        setLoading(true);
        try {
            const response = await GeneralApiService.getSalesSchedule();
            const matrix = {};
            DAYS.forEach((_, i) => matrix[i] = new Set());

            response.forEach(block => {
                const startHour = parseInt(block.start_time.split(':')[0], 10);
                const endHour = parseInt(block.end_time.split(':')[0], 10);
                const adjustedEndHour = (block.end_time.includes('59:59') || block.end_time.includes('59')) ? endHour + 1 : endHour;

                for (let h = startHour; h < adjustedEndHour; h++) {
                    if (matrix[block.day_of_week]) {
                        matrix[block.day_of_week].add(h);
                    }
                }
            });
            setScheduleMatrix(matrix);
        } catch (err) {
            console.log(err);
            // TOASTR ERROR with Custom ID
            toast.error('Failed to load schedule.', { toastId: 'sales-schedule-fetch-error' });
        } finally {
            setLoading(false);
        }
    };

    const toggleSlot = (dayIndex, hour) => {
        const newMatrix = { ...scheduleMatrix };
        if (!newMatrix[dayIndex]) newMatrix[dayIndex] = new Set();
        const daySet = new Set(newMatrix[dayIndex]);
        if (daySet.has(hour)) {
            daySet.delete(hour);
        } else {
            daySet.add(hour);
        }
        newMatrix[dayIndex] = daySet;
        setScheduleMatrix(newMatrix);
    };

    const handleSave = async () => {
        setSaving(true);
        const payload = [];

        Object.keys(scheduleMatrix).forEach(dayStr => {
            const dayIndex = parseInt(dayStr, 10);
            const hours = Array.from(scheduleMatrix[dayIndex]).sort((a, b) => a - b);

            hours.forEach(hour => {
                const startStr = `${String(hour).padStart(2, '0')}:00:00`;
                const endStr = `${String(hour).padStart(2, '0')}:59:59`;
                payload.push({
                    day_of_week: dayIndex,
                    start_time: startStr,
                    end_time: endStr
                });
            });
        });

        try {
            await GeneralApiService.updateSalesSchedule({ schedules: payload });
            // TOASTR SUCCESS with Custom ID
            toast.info('Schedule updated successfully!', { toastId: 'sales-schedule-save-success' });
        } catch (err) {
            // TOASTR ERROR with Custom ID
            toast.error('Failed to save schedule.', { toastId: 'sales-schedule-save-error' });
        } finally {
            setSaving(false);
        }
    };

    const toggleAllDay = (dayIndex, select) => {
        const newMatrix = { ...scheduleMatrix };
        if(select) {
            newMatrix[dayIndex] = new Set([9,10,11,12,13,14,15,16]);
        } else {
            newMatrix[dayIndex] = new Set();
        }
        setScheduleMatrix(newMatrix);
    }

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

    return (
        <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography level="h4">Weekly Schedule</Typography>
                    <Typography level="body-sm">Select the hours you are available to take calls. Blue indicates active.</Typography>
                </Box>
                <Button
                    loading={saving}
                    onClick={handleSave}
                    startDecorator={<SaveIcon />}
                    color="primary"
                >
                    Save Changes
                </Button>
            </Box>

            <Sheet variant="outlined" sx={{ overflowX: 'auto', borderRadius: 'sm'}}>
                <Table stickyHeader hoverRow stripe="odd">
                    <thead>
                    <tr>
                        <th style={{ width: 80, textAlign: 'center' }}>Time</th>
                        {DAYS.map((day, i) => (
                            <th key={day} style={{ textAlign: 'center', minWidth: 100 }}>
                                <Box sx={{display:'flex', flexDirection:'column', alignItems:'center'}}>
                                    {day}
                                    <Box sx={{display:'flex', gap: 1, mt: 0.5}}>
                                        <Link level="body-xs" onClick={() => toggleAllDay(i, true)}>9-5</Link>
                                        <Link level="body-xs" color="danger" onClick={() => toggleAllDay(i, false)}>Clear</Link>
                                    </Box>
                                </Box>
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {HOURS.map((hour) => (
                        <tr key={hour}>
                            <td style={{ fontWeight: 'bold', color: 'gray', textAlign:'center' }}>
                                {`${String(hour).padStart(2, '0')}:00`}
                            </td>
                            {DAYS.map((_, dayIndex) => {
                                const isActive = scheduleMatrix[dayIndex]?.has(hour);
                                return (
                                    <td key={`${dayIndex}-${hour}`} style={{ textAlign: 'center', padding: 0 }}>
                                        <Box
                                            onClick={() => toggleSlot(dayIndex, hour)}
                                            sx={{
                                                height: '40px',
                                                width: '100%',
                                                cursor: 'pointer',
                                                bgcolor: isActive ? 'primary.500' : 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                transition: 'background-color 0.1s',
                                                '&:hover': {
                                                    bgcolor: isActive ? 'primary.600' : 'neutral.100'
                                                }
                                            }}
                                        >
                                            {isActive && <TaskAltRoundedIcon sx={{ fontSize: 20, color: 'white' }} />}
                                        </Box>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </Sheet>
        </Stack>
    );
};

// --- Main Page Component ---

export default function MySales() {
    // --- State for Performance Data ---
    const [salesData, setSalesData] = useState(null); // { monthlyStats: {}, weeklyPayroll: [], summary: {} }
    const [selectedMonth, setSelectedMonth] = useState('');
    const [statsLoading, setStatsLoading] = useState(true);
    const [statsError, setStatsError] = useState(null);

    // --- Fetch Stats Logic ---
    useEffect(() => {
        const fetchStats = async () => {
            setStatsLoading(true);
            try {
                const apiResponse = await BookingApiService.getSalesStats();
                if (apiResponse && apiResponse.monthlyStats) {
                    setSalesData(apiResponse);
                    // Select first month (most recent) by default
                    const months = Object.keys(apiResponse.monthlyStats);
                    if (months.length > 0) {
                        setSelectedMonth(months[0]);
                    }
                } else {
                    setStatsError('No sales data found for your account.');
                }
            } catch (err) {
                setStatsError('Failed to fetch sales data.');
            } finally {
                setStatsLoading(false);
            }
        };
        fetchStats();
    }, []);

    const formatMonthForDisplay = (monthStr) => {
        if (!monthStr) return '';
        const [monthName, year] = monthStr.split('-');
        return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;
    };

    return (
        <>
            {/* Toast Container for displaying notifications */}
            <ToastContainer theme="colored" position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Breadcrumbs size="sm" separator={<ChevronRightRoundedIcon />} sx={{ pl: 0 }}>
                    <Link underline="none" color="neutral" href="/dashboard"><HomeRoundedIcon /></Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>Dashboard</Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>My Sales</Typography>
                </Breadcrumbs>
                <PayoutTimer />
            </Box>

            <Typography level="h2" component="h1" sx={{ mb: 2 }}>Sales Dashboard</Typography>

            <Tabs defaultValue={0}>
                <TabList
                    sx={{
                        mb: 3,
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        [`&& .${tabClasses.root}`]: {
                            flex: 'initial',
                            bgcolor: 'transparent',
                            fontWeight: 600,
                            flexShrink: 0,
                            '&:hover': {bgcolor: 'transparent'},
                            [`&.${tabClasses.selected}`]: {
                                color: 'primary.plainColor',
                                '&::after': {
                                    height: 2,
                                    borderTopLeftRadius: 3,
                                    borderTopRightRadius: 3,
                                    bgcolor: 'primary.500',
                                },
                            },
                        },
                    }}
                >
                    <Tab disableIndicator >
                        <CalendarMonthIcon sx={{ mr: 1, fontSize: { xs: 18, sm: 20 } }} /> <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>My </Box>Schedule
                    </Tab>
                    <Tab disableIndicator>
                        <QueryStatsRoundedIcon sx={{ mr: 1, fontSize: { xs: 18, sm: 20 } }} /> Performance
                    </Tab>
                </TabList>

                <TabPanel value={0} sx={{p: 0.5}} >
                    <ScheduleTab />
                </TabPanel>

                <TabPanel value={1}  sx={{p: 0.5}}>
                    <PerformanceTab
                        loading={statsLoading}
                        error={statsError}
                        salesData={salesData}
                        selectedMonth={selectedMonth}
                        setSelectedMonth={setSelectedMonth}
                        formatMonthForDisplay={formatMonthForDisplay}
                        weeklyData={salesData?.weeklyPayroll}
                        summary={salesData?.summary}
                    />
                </TabPanel>
            </Tabs>
        </>
    );
}
